import { useNavigate, useLocation } from "react-router-dom";
import { 
  Home,
  LayoutGrid, 
  QrCode, 
  UserCircle, 
  Users, 
  Ticket as TicketIcon 
} from "lucide-react";
import { useSite } from "../../../store/useSite";
import { useBase } from "../../../store/useBase";

const NAV_ITEMS = [
  { id: "home", label: "HOME", icon: Home },
  { id: "leads", label: "LEADS", icon: Users },
  { id: "scan", label: "SCAN QR", icon: QrCode, isAction: true },
  { id: "ticket", label: "TICKET", icon: TicketIcon },
  { id: "profile", label: "PROFILE", icon: UserCircle },
];

export default function AttendeeMobileNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { siteUser, profileSettingPopup, setProfileSettingPopup, siteUserLoading } = useSite();
  const { siteSlug } = useBase();

  // ONLY show for attendee (role 26) and ON mobile
  if (siteUserLoading || siteUser?.role_id !== 26) return null;

  const handleNavClick = (item) => {
    // Close profile popup when navigating to other tabs
    if (item.id !== "profile") {
      setProfileSettingPopup(false);
    }

    if (item.id === "profile") {
      setProfileSettingPopup(!profileSettingPopup);
      return;
    }

    if (item.id === "scan") {
      navigate(`/${siteSlug}/dashboard`, { state: { openScanner: true, activeTab: "scan" } });
      return;
    }

    const pathMap = {
      leads: `/${siteSlug}/dashboard`,
      ticket: `/${siteSlug}/dashboard`,
      home: `/${siteSlug}/`,
    };

    const stateMap = {
      leads: { activeTab: "scan" },
      ticket: { activeTab: "dashboard" },
    };

    navigate(pathMap[item.id] || `/${siteSlug}/`, { state: stateMap[item.id] });
  };

  return (
    <div className="lg:hidden w-full fixed bottom-0 left-0 z-[100] flex justify-center items-center pointer-events-none pb-safe pb-[env(safe-area-inset-bottom,0px)]">
      <nav className="h-[70px] bg-white/95 backdrop-blur-3xl px-1 py-1 sm:px-2 border-t border-gray-100 grid grid-cols-5 items-center w-full max-w-lg pointer-events-auto transition-all duration-300 shadow-[0_-8px_30px_rgb(0,0,0,0.04)] rounded-t-[32px]">
        {NAV_ITEMS.map((item) => {
          const isDashboard = location.pathname.includes("/dashboard");
          const currentTab = location.state?.activeTab || "dashboard";
          
          const isActive = profileSettingPopup 
            ? item.id === "profile" 
            : (
                (item.id === "leads" && isDashboard && currentTab === "scan") ||
                (item.id === "ticket" && isDashboard && currentTab === "dashboard") ||
                (item.id === "home" && !isDashboard)
              );

          if (item.isAction) {
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className="flex flex-col items-center gap-1 group -mt-12"
              >
                <div 
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center border-[4px] border-white transition-all transform active:scale-90 group-hover:scale-105 duration-300 shadow-lg ${
                    isActive && !profileSettingPopup ? "bg-blue-700 shadow-blue-200" : "bg-blue-600 shadow-blue-100"
                  }`}
                >
                  <item.icon size={24} strokeWidth={2.5} className="text-white" />
                </div>
                <span className={`text-[9px] font-black tracking-tight mt-0.5 uppercase ${isActive && !profileSettingPopup ? "text-blue-700" : "text-blue-600"}`}>
                  {item.label}
                </span>
              </button>
            );
          }

          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              className={`flex flex-col items-center justify-center py-3 transition-all duration-300 gap-1.5 ${
                isActive ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <div className={`relative flex items-center justify-center transition-all duration-300 ${isActive ? "scale-110" : ""}`}>
                <item.icon
                  size={20}
                  strokeWidth={isActive ? 2.5 : 2}
                  className="transition-colors duration-300"
                />
                {isActive && (
                  <div className="absolute -bottom-1.5 w-1 h-1 rounded-full bg-blue-600 animate-pulse" />
                )}
              </div>
              <span className={`text-[8px] sm:text-[9px] font-black tracking-tighter uppercase transition-colors duration-300 ${isActive ? "text-blue-600" : "text-gray-400"}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
